/**
 * Page_Metadata 的纯函数层。
 *
 * 五个导出各自守住一条不变量，运行时 `src/utils/seo.ts`、构建期
 * `scripts/prerender/*` 与巡检器 `scripts/geo-inspect/*` 共用同一份实现：
 *
 * - `clampTitle()`：`<title>` / `og:title` / `twitter:title` 恒为 20–60 字符（需求 2.2、2.18）
 * - `clampDescription()`：description 系列恒为 70–160 字符且不残留占位符（需求 2.3、2.15）
 * - `buildKeywords()`：`<meta name="keywords">` 恒为 3–10 个逗号分隔关键词、总长 ≤200（需求 2.16）
 * - `normalizeCanonical()`：canonical / `og:url` 恒为自指的 WWW_Domain 绝对地址且幂等
 *   （需求 2.1、2.5、13.4、13.11）
 * - `buildPageMetadata()`：把上述四者组装成 title / description / keywords / canonical /
 *   robots / 7 个 og / 4 个 twitter 的完整取值集合（需求 2.1–2.7、2.15–2.18）
 *
 * 全部为纯函数：不读 DOM、不发网络请求、不读除 `import.meta.env` 外的全局状态，
 * 对相同输入永远返回相同输出，Node 与浏览器结果一致。
 * 兜底文案与 Page_Type 差异一律读 `PAGE_TYPE_META_PRESET`，不写 Page_Type 分支，
 * 新增页面类型只需在常量表里补一行（`Record<PageType, …>` 会在编译期提醒）。
 * 长度一律按 code point 计（中文 1 字符，代理对不被算作 2）。
 *
 * 对应需求：2.1、2.2、2.3、2.4、2.5、2.6、2.7、2.15、2.16、2.17、2.18、13.4、13.11
 */

import { PAGE_TYPE_META_PRESET, SITE_ORIGIN } from "./constants";
import { clampJsonLdText, toAbsoluteUrl } from "./jsonld/serialize";
import { PAGE_TYPES } from "./types";
import type { OgType, PageType, PageTypePreset } from "./types";
import { extractVisibleText } from "./visibleText";

/** `<title>` 去首尾空格后的长度闭区间（需求 2.2） */
export const TITLE_LENGTH = { min: 20, max: 60 } as const;

/** description 系列取值去首尾空格后的长度闭区间（需求 2.3） */
export const DESCRIPTION_LENGTH = { min: 70, max: 160 } as const;

/** `<meta name="keywords">` 的构成约束（需求 2.16） */
export const KEYWORDS_LIMIT = {
  /** 关键词个数下限 */
  minCount: 3,
  /** 关键词个数上限 */
  maxCount: 10,
  /** 单个关键词长度下限 */
  minLength: 2,
  /** 单个关键词长度上限 */
  maxLength: 20,
  /** `content` 属性值总长度上限 */
  maxTotalLength: 200,
} as const;

/** 可收录页面的 `<meta name="robots">` 取值：含 index/follow、不含 noindex/nofollow（需求 2.7） */
export const INDEXABLE_ROBOTS =
  "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

/** 不可收录变体（未知分类页、404 变体）的 robots 取值（需求 13.11、11.10） */
export const NOINDEX_ROBOTS = "noindex,nofollow";

/** `og:locale` 固定取值（需求 2.4） */
export const OG_LOCALE = "zh_CN";

/** `twitter:card` 固定取值（需求 2.6） */
export const TWITTER_CARD = "summary_large_image";

/** 站点名兜底值：`siteConfig` 缺失或站点级 SEO 数据源失败时使用（需求 2.17） */
export const FALLBACK_SITE_NAME = "欧格登智能科技";

/** `pageType` 缺省时使用的预设，`HOME` 的文案覆盖面最广 */
const DEFAULT_PAGE_TYPE: PageType = "HOME";

/** 标题补足到下限时循环取用的兜底语料，非空白、不含占位符 */
const GENERIC_TITLE_PAD = "欧格登智能科技企业级解决方案与产品服务";

/** 关键词个数不足且 Page_Type 预设种子也用尽时的最后一层来源，每项 2–20 字符 */
const GENERIC_KEYWORD_SEEDS: readonly string[] = [
  "欧格登",
  "欧格登智能科技",
  "信息安全",
  "IT基础架构",
  "企业级解决方案",
];

/** 长度仍不足时逐字符补齐用的填充符，非空白字符，保证结果不以空白结尾 */
const FILLER_CHAR = "。";

/** 标题拼接与补足统一使用的分隔符 */
const TITLE_JOINER = " - ";

/**
 * 标题截断的优选断点集合。
 *
 * 需求 2.2 只要求「超过 60 时截断至不超过 60」，这里额外优先在分隔符处断开，
 * 避免把「零信任架构设计」这类词组腰斩成半个词。
 */
const TITLE_SEPARATORS = new Set([
  "|",
  "-",
  "–",
  "—",
  "－",
  "·",
  "•",
  "/",
  "、",
  "，",
  ",",
  "。",
  "；",
  ";",
  "：",
  ":",
  ")",
  "）",
  "]",
  "】",
  " ",
  "\u3000",
]);

/** 关键词来源字符串的分隔符：中英文逗号、分号、顿号与竖线都视为分隔 */
const KEYWORD_SPLIT_PATTERN = /[,，、;；|\r\n\t]+/;

/**
 * 未替换的占位符标记，如 `{title}`、`{{name}}`、`${name}`。
 *
 * 需求 2.15 要求兜底文案「不包含未替换的占位符标记」。模板替换由本模块与
 * `PAGE_TYPE_META_PRESET` 共同保证，但来源字段本身也可能带着上游模板的残留，
 * 因此在清洗阶段统一抹掉整对花括号及其内容。
 */
const PLACEHOLDER_PATTERN = /\$?\{{1,2}[^{}]*\}{1,2}/g;

/** 成对花括号被抹掉后可能剩下的孤立花括号 */
const STRAY_BRACE_PATTERN = /[{}]/g;

/** Page_Type 判定：非法取值回落到默认预设，而不是抛异常 */
function isPageType(value: unknown): value is PageType {
  return typeof value === "string" && (PAGE_TYPES as readonly string[]).includes(value);
}

/** 读取 Page_Type 预设，`pageType` 非法或缺失时回落到 `HOME` */
function resolvePreset(pageType: unknown): PageTypePreset {
  return PAGE_TYPE_META_PRESET[isPageType(pageType) ? pageType : DEFAULT_PAGE_TYPE];
}

/** 按 code point 切分，避免代理对被截断成半个字符 */
function toCodePoints(text: string): string[] {
  return Array.from(text);
}

/**
 * 把任意来源字段清成可直接进入 `<meta>` 的纯文本。
 *
 * 流水线：去 `script`/`style`/`noscript` 与全部标签 → 解码实体 → 折叠空白 → 去首尾空白
 * （复用 `extractVisibleText()`，与 Visible_Text_Length 同口径）→ 抹掉未替换的占位符标记。
 */
function sanitizeText(raw: unknown): string {
  if (typeof raw !== "string" || raw === "") {
    return "";
  }

  const visible = extractVisibleText(raw);
  if (visible.indexOf("{") < 0 && visible.indexOf("}") < 0) {
    return visible;
  }

  const withoutPlaceholder = visible
    .replace(PLACEHOLDER_PATTERN, " ")
    .replace(STRAY_BRACE_PATTERN, " ");

  // 抹除后可能留下连续空白，再走一次折叠与去首尾空白
  return extractVisibleText(withoutPlaceholder);
}

/** 去掉尾部空白与分隔符，使截断结果不以「 - 」「，」之类的残尾收口 */
function trimTrailingEdge(chars: string[]): void {
  while (chars.length > 0) {
    const last = chars[chars.length - 1];
    if (/\s/.test(last) || TITLE_SEPARATORS.has(last)) {
      chars.pop();
      continue;
    }
    break;
  }
}

/** 用一段语料循环补齐到下限，保证补足过程一定终止 */
function padCyclically(chars: string[], min: number, source: string): void {
  const pad = toCodePoints(source);

  if (pad.length === 0) {
    while (chars.length < min) {
      chars.push(FILLER_CHAR);
    }
    return;
  }

  let index = 0;
  while (chars.length < min) {
    chars.push(pad[index % pad.length]);
    index += 1;
  }
}

/**
 * 从 `titleTemplate` 取出「{name} 之后的后缀」，如
 * `"{name} - 企业级网络安全与IT基础架构解决方案"` → `"企业级网络安全与IT基础架构解决方案"`。
 * 分隔符由 `appendTitleSegment()` 统一补，这里只要正文。
 */
function presetTitleSuffix(preset: PageTypePreset): string {
  const parts = preset.titleTemplate.split("{name}");
  const tail = parts.length > 1 ? parts[parts.length - 1] : "";
  const chars = toCodePoints(sanitizeText(tail));

  while (chars.length > 0 && (/\s/.test(chars[0]) || TITLE_SEPARATORS.has(chars[0]))) {
    chars.shift();
  }
  trimTrailingEdge(chars);

  return chars.join("");
}

/** 追加一段标题补足语料；已包含该语料时跳过，避免「欧格登 - 欧格登」式重复 */
function appendTitleSegment(chars: string[], segment: string): void {
  const text = sanitizeText(segment);
  if (text === "") {
    return;
  }

  const current = chars.join("");
  if (current.includes(text)) {
    return;
  }

  if (current !== "") {
    chars.push(...toCodePoints(TITLE_JOINER));
  }
  chars.push(...toCodePoints(text));
}

/**
 * 把超过上限的标题截断至 ≤ `TITLE_LENGTH.max`，优先在分隔符处断开。
 *
 * 断点必须落在 `TITLE_LENGTH.min` 之后，否则截断反而会跌破下限；
 * 找不到合适断点时按上限硬截。
 */
function truncateTitle(chars: string[]): string {
  const head = chars.slice(0, TITLE_LENGTH.max);

  for (let index = head.length - 1; index >= TITLE_LENGTH.min; index -= 1) {
    if (!TITLE_SEPARATORS.has(head[index])) {
      continue;
    }
    const candidate = head.slice(0, index);
    trimTrailingEdge(candidate);
    if (candidate.length >= TITLE_LENGTH.min) {
      return candidate.join("");
    }
  }

  trimTrailingEdge(head);
  if (head.length < TITLE_LENGTH.min) {
    padCyclically(head, TITLE_LENGTH.min, GENERIC_TITLE_PAD);
  }
  return head.join("");
}

/** 站点名归一化：空/纯空白回落到兜底站点名（需求 2.17） */
function resolveSiteName(siteName: unknown): string {
  return sanitizeText(siteName) || FALLBACK_SITE_NAME;
}

/**
 * 把标题来源文本归一化到 20–60 字符（需求 2.2、2.18）。
 *
 * - 来源为空/纯空白/仅含占位符 → 用 `PAGE_TYPE_META_PRESET[pageType].fallbackTitle`
 *   兜底（需求 2.18，调用方可据 `buildPageMetadata()` 返回的 `omittedFields` 记日志）
 * - 长度不足 20 → 依次拼接站点名、Page_Type 预设标题后缀、预设兜底标题补足
 * - 长度超过 60 → 优先在分隔符处截断至 ≤60
 *
 * 返回值恒不含 HTML 标签与未替换占位符、不以空白开头或结尾，
 * 因此调用方再 `trim()` 也不会跌破下限。
 *
 * @param raw 内容标题字段，可为空值、富文本片段或超长文本
 * @param siteName 站点名，缺失时用 `FALLBACK_SITE_NAME`
 * @param pageType 页面类型，决定兜底标题与后缀；缺省或非法取值回落 `HOME`
 */
export function clampTitle(raw?: unknown, siteName?: unknown, pageType?: PageType): string {
  const preset = resolvePreset(pageType);
  const site = resolveSiteName(siteName);
  const base = sanitizeText(raw);
  const chars = toCodePoints(base !== "" ? base : sanitizeText(preset.fallbackTitle));

  if (chars.length < TITLE_LENGTH.min) {
    const segments = [site, presetTitleSuffix(preset), preset.fallbackTitle, GENERIC_TITLE_PAD];
    for (const segment of segments) {
      if (chars.length >= TITLE_LENGTH.min) {
        break;
      }
      appendTitleSegment(chars, segment);
    }
    padCyclically(chars, TITLE_LENGTH.min, GENERIC_TITLE_PAD);
  }

  if (chars.length > TITLE_LENGTH.max) {
    return truncateTitle(chars);
  }

  return chars.join("");
}

/**
 * 把描述来源文本归一化到 70–160 字符（需求 2.3、2.15）。
 *
 * 超过 160 时截断；不足 70 时用 `PAGE_TYPE_META_PRESET[pageType].descriptionTemplate`
 * 把 `{name}` 换成该页标题后拼接补足（`title` 缺失时用预设面包屑标签），
 * 补足与截断复用 `clampJsonLdText()`，与 JSON-LD 文本同一套流水线。
 *
 * 返回值恒不含 HTML 标签、不含未替换的占位符标记、不以空白开头或结尾。
 *
 * @param raw 内容描述字段，可为空值、富文本片段或超长文本
 * @param pageType 页面类型，决定补足模板；缺省或非法取值回落 `HOME`
 * @param title 该页标题，用于替换模板中的名称占位符
 */
export function clampDescription(raw?: unknown, pageType?: PageType, title?: unknown): string {
  const resolvedPageType = isPageType(pageType) ? pageType : DEFAULT_PAGE_TYPE;
  const name = sanitizeText(title);

  const clamped = clampJsonLdText(
    sanitizeText(raw),
    DESCRIPTION_LENGTH.min,
    DESCRIPTION_LENGTH.max,
    { pageType: resolvedPageType, name },
  );

  // 补足语料来自本模块与常量表，理论上不含花括号；仍复查一遍，
  // 保证「不残留占位符」这条不变量与补足实现解耦
  if (clamped.indexOf("{") < 0 && clamped.indexOf("}") < 0) {
    return clamped;
  }

  return clampJsonLdText(sanitizeText(clamped), DESCRIPTION_LENGTH.min, DESCRIPTION_LENGTH.max, {
    pageType: resolvedPageType,
    name,
  });
}

/** 把关键词来源（字符串或数组）拆成候选项 */
function toKeywordCandidates(raw: unknown): string[] {
  if (typeof raw === "string") {
    return raw.split(KEYWORD_SPLIT_PATTERN);
  }

  if (Array.isArray(raw)) {
    const flat: string[] = [];
    for (const item of raw) {
      flat.push(...toKeywordCandidates(item));
    }
    return flat;
  }

  return [];
}

/**
 * 归一化单个关键词：清洗 → 超长按 code point 截断至 20 → 过短丢弃。
 * 返回空串表示该候选项不可用。
 */
function normalizeKeyword(candidate: unknown): string {
  const chars = toCodePoints(sanitizeText(candidate));

  if (chars.length > KEYWORDS_LIMIT.maxLength) {
    chars.length = KEYWORDS_LIMIT.maxLength;
    while (chars.length > 0 && /\s/.test(chars[chars.length - 1])) {
      chars.pop();
    }
  }

  return chars.length >= KEYWORDS_LIMIT.minLength ? chars.join("") : "";
}

/**
 * 把关键词来源构造成 `<meta name="keywords">` 的 `content` 取值（需求 2.16）。
 *
 * - 来源可为逗号分隔字符串或字符串数组，混杂重复项、空串与超长项都能接受
 * - 每项清洗后超长截断至 20 字符、不足 2 字符丢弃，按小写形态去重
 * - 个数不足 3 时用 `PAGE_TYPE_META_PRESET[pageType].keywordSeeds` 补齐
 * - 个数超过 10 时按顺序取前 10；总长超过 200 时从尾部继续裁剪（下限 3 个仍成立：
 *   3 个各 ≤20 字符加 2 个逗号最多 62 字符，不可能越界）
 *
 * 返回值恒为英文逗号分隔、无空格的 3–10 项，总长 ≤200。
 */
export function buildKeywords(raw?: unknown, pageType?: PageType): string {
  const preset = resolvePreset(pageType);
  const seen = new Set<string>();
  const keywords: string[] = [];

  const collect = (candidates: readonly unknown[], limit: number): void => {
    for (const candidate of candidates) {
      if (keywords.length >= limit) {
        return;
      }
      const keyword = normalizeKeyword(candidate);
      if (keyword === "") {
        continue;
      }
      const dedupeKey = keyword.toLowerCase();
      if (seen.has(dedupeKey)) {
        continue;
      }
      seen.add(dedupeKey);
      keywords.push(keyword);
    }
  };

  collect(toKeywordCandidates(raw), KEYWORDS_LIMIT.maxCount);

  if (keywords.length < KEYWORDS_LIMIT.minCount) {
    collect(preset.keywordSeeds, KEYWORDS_LIMIT.minCount);
  }
  if (keywords.length < KEYWORDS_LIMIT.minCount) {
    collect(GENERIC_KEYWORD_SEEDS, KEYWORDS_LIMIT.minCount);
  }
  // 预设种子与通用种子都被去重掉的极端输入：用序号造出可用项，保证个数下限
  while (keywords.length < KEYWORDS_LIMIT.minCount) {
    const filler = `${FALLBACK_SITE_NAME}${keywords.length + 1}`;
    const keyword = normalizeKeyword(filler);
    if (seen.has(keyword.toLowerCase())) {
      break;
    }
    seen.add(keyword.toLowerCase());
    keywords.push(keyword);
  }

  while (
    keywords.length > KEYWORDS_LIMIT.minCount &&
    keywords.join(",").length > KEYWORDS_LIMIT.maxTotalLength
  ) {
    keywords.pop();
  }

  return keywords.join(",");
}

/** `new URL()` 的安全包装：解析失败返回 `null` 而不抛异常 */
function tryParseUrl(input: string): URL | null {
  try {
    return new URL(input);
  } catch {
    return null;
  }
}

/**
 * 组装 canonical 的查询串。
 *
 * `SOLUTION_CATEGORY` 保留且仅保留 `category`（需求 2.1、13.4），
 * 其余 Page_Type 丢弃全部查询参数 —— 因此 `utm_*` / `from` / `spm`
 * 这些追踪参数在任何 Page_Type 下都不可能出现在输出里。
 */
function buildCanonicalQuery(pageType: PageType, params: URLSearchParams): string {
  if (pageType !== "SOLUTION_CATEGORY") {
    return "";
  }

  const category = (params.get("category") ?? "").trim();
  if (category === "") {
    return "";
  }

  return `?category=${encodeURIComponent(category)}`;
}

/**
 * 把任意页面地址归一化为该页自指的 canonical（需求 2.1、2.5、13.4、13.11）。
 *
 * - 相对路径、其他来源的绝对地址都被收敛到 `https://www.ogolden.com`
 *   （复用 `toAbsoluteUrl()`）
 * - 去掉 URL fragment
 * - `SOLUTION_CATEGORY` 仅保留 `category` 参数，其余 Page_Type 丢弃全部查询参数，
 *   `utm_*` / `from` / `spm` 由此必然不出现
 * - `HOME` 以 `/` 结尾，其余 Page_Type 的路径不以 `/` 结尾
 *
 * 输出恒以 `https://www.ogolden.com` 开头，且
 * `normalizeCanonical(t, normalizeCanonical(t, x))` 与 `normalizeCanonical(t, x)` 相等（幂等）。
 *
 * @param pageType 页面类型；非法取值按 `HOME` 之外的普通页面处理（不追加尾斜杠）
 * @param path 页面路径或绝对地址，可带 fragment 与追踪参数
 */
export function normalizeCanonical(pageType: PageType, path?: unknown): string {
  const absolute = toAbsoluteUrl(typeof path === "string" ? path : "");
  const parsed = tryParseUrl(absolute);

  if (!parsed) {
    return pageType === "HOME" ? `${SITE_ORIGIN}/` : SITE_ORIGIN;
  }

  let pathname = (parsed.pathname || "/").replace(/\/{2,}/g, "/");
  if (!pathname.startsWith("/")) {
    pathname = `/${pathname}`;
  }

  if (pageType === "HOME") {
    pathname = pathname.endsWith("/") ? pathname : `${pathname}/`;
  } else {
    pathname = pathname.replace(/\/+$/, "");
  }

  return `${SITE_ORIGIN}${pathname}${buildCanonicalQuery(pageType, parsed.searchParams)}`;
}

/** 单个页面的内容来源字段，全部可缺失（缺失走 Page_Type 预设兜底） */
export type PageMetadataContent = {
  /** 内容标题，空/纯空白时用预设兜底标题（需求 2.18） */
  title?: string | null;
  /** 内容描述，空/纯空白时用预设模板与标题拼接补足（需求 2.15） */
  description?: string | null;
  /** 内容关键词，逗号分隔字符串或字符串数组 */
  keywords?: string | readonly string[] | null;
  /** 页面路径或绝对地址，用于 canonical 与 `og:url` */
  path?: string | null;
  /** 主图地址，空时用 `PAGE_TYPE_META_PRESET[pageType].fallbackOgImage` */
  image?: string | null;
  /** 是否输出 `noindex,nofollow`（未知分类页与 404 变体，需求 13.11、11.10） */
  noindex?: boolean;
  /** 覆盖 Page_Type 预设的 `og:type`（需求 2.8、2.9） */
  ogType?: OgType;
};

/** 站点级 SEO 配置；读取失败或超时时整体缺省，由本模块的兜底常量接管（需求 2.17） */
export type SiteMetaConfig = {
  /** 站点名，`og:site_name` 取值，全站一致（需求 2.4） */
  siteName?: string | null;
  /** 后台 `siteTitle` 字段，`siteName` 缺失时作为站点名来源 */
  siteTitle?: string | null;
  /** 站点级关键词，与内容关键词合并 */
  siteKeywords?: string | readonly string[] | null;
  /** 站点级描述，内容描述缺失时的第一层来源 */
  siteDescription?: string | null;
  /** 站点级默认社交分享图 */
  defaultOgImage?: string | null;
};

/** 7 个 Open Graph 元素的取值（需求 2.4、2.5、2.8、2.9） */
export type OpenGraphMetadata = {
  "og:type": OgType;
  "og:title": string;
  "og:description": string;
  /** 恒等于同页 canonical（需求 2.5） */
  "og:url": string;
  "og:image": string;
  "og:site_name": string;
  "og:locale": typeof OG_LOCALE;
};

/** 4 个 Twitter Card 元素的取值（需求 2.6） */
export type TwitterMetadata = {
  "twitter:card": typeof TWITTER_CARD;
  "twitter:title": string;
  "twitter:description": string;
  "twitter:image": string;
};

/** 单个页面的完整 Page_Metadata */
export type PageMetadata = {
  /** 实际生效的 Page_Type（入参非法时为回落值） */
  pageType: PageType;
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  robots: string;
  og: OpenGraphMetadata;
  twitter: TwitterMetadata;
  /**
   * 走了兜底的来源字段名，供调用方记日志（需求 2.15、2.17、2.18）。
   * 取值为 `title` / `description` / `keywords` / `image` / `path` / `siteName`。
   */
  omittedFields: string[];
};

/** 图片地址归一化：空/纯空白时按 `siteConfig` → Page_Type 预设 → 全站兜底顺序回落 */
function resolveImage(
  content: PageMetadataContent,
  siteConfig: SiteMetaConfig,
  preset: PageTypePreset,
): { image: string; omitted: boolean } {
  const candidates = [content.image, siteConfig.defaultOgImage];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return { image: toAbsoluteUrl(candidate.trim()), omitted: candidate !== content.image };
    }
  }

  return { image: toAbsoluteUrl(preset.fallbackOgImage), omitted: true };
}

/**
 * 组装单个页面的完整 Page_Metadata（需求 2.1–2.7、2.15–2.18）。
 *
 * 返回值满足的不变量：
 * - `title` 去首尾空格后 20–60 字符，`og:title` / `twitter:title` 与之逐字符一致
 * - `description` 70–160 字符且不含未替换占位符，`og:description` / `twitter:description`
 *   与之逐字符一致
 * - `keywords` 为 3–10 个英文逗号分隔关键词、每项 2–20 字符、总长 ≤200
 * - `canonical` 为该页自指的 WWW_Domain 绝对地址，`og:url` 与之逐字符相等
 * - `og:locale` 为 `zh_CN`、`twitter:card` 为 `summary_large_image`、
 *   `og:site_name` 全站一致、`og:image` / `twitter:image` 为绝对地址
 * - `robots` 含 `index` 与 `follow`（`content.noindex` 为真时改为 `noindex,nofollow`）
 *
 * 站点级 SEO 数据源失败或超时时，调用方只需传空的 `siteConfig`：
 * 兜底常量与 Page_Type 预设会接管全部取值，上述长度与数量约束依旧成立（需求 2.17）。
 *
 * @param pageType 页面类型，决定预设文案、`og:type` 与 canonical 的尾斜杠规则
 * @param content 内容来源字段，全部可缺失
 * @param siteConfig 站点级 SEO 配置，可整体缺失
 */
export function buildPageMetadata(
  pageType: PageType,
  content: PageMetadataContent = {},
  siteConfig: SiteMetaConfig = {},
): PageMetadata {
  const resolvedPageType = isPageType(pageType) ? pageType : DEFAULT_PAGE_TYPE;
  const preset = PAGE_TYPE_META_PRESET[resolvedPageType];
  const omittedFields: string[] = [];

  const siteNameSource = sanitizeText(siteConfig.siteName) || sanitizeText(siteConfig.siteTitle);
  if (siteNameSource === "") {
    omittedFields.push("siteName");
  }
  const siteName = siteNameSource || FALLBACK_SITE_NAME;

  if (sanitizeText(content.title) === "") {
    omittedFields.push("title");
  }
  const title = clampTitle(content.title, siteName, resolvedPageType);

  const descriptionSource =
    sanitizeText(content.description) || sanitizeText(siteConfig.siteDescription);
  if (sanitizeText(content.description) === "") {
    omittedFields.push("description");
  }
  const description = clampDescription(descriptionSource, resolvedPageType, title);

  const keywordSources: unknown[] = [content.keywords, siteConfig.siteKeywords];
  if (toKeywordCandidates(content.keywords).every((item) => normalizeKeyword(item) === "")) {
    omittedFields.push("keywords");
  }
  const keywords = buildKeywords(keywordSources, resolvedPageType);

  if (typeof content.path !== "string" || content.path.trim() === "") {
    omittedFields.push("path");
  }
  const canonical = normalizeCanonical(resolvedPageType, content.path);

  const { image, omitted: imageOmitted } = resolveImage(content, siteConfig, preset);
  if (imageOmitted) {
    omittedFields.push("image");
  }

  const ogType: OgType = content.ogType ?? preset.ogType;
  const robots = content.noindex === true ? NOINDEX_ROBOTS : INDEXABLE_ROBOTS;

  return {
    pageType: resolvedPageType,
    title,
    description,
    keywords,
    canonical,
    robots,
    og: {
      "og:type": ogType,
      "og:title": title,
      "og:description": description,
      // 需求 2.5：og:url 与 canonical 必须逐字符相等，这里直接复用同一个值
      "og:url": canonical,
      "og:image": image,
      "og:site_name": siteName,
      "og:locale": OG_LOCALE,
    },
    twitter: {
      "twitter:card": TWITTER_CARD,
      "twitter:title": title,
      "twitter:description": description,
      "twitter:image": image,
    },
    omittedFields,
  };
}
